import { DS_COLOR, DS_COLOR_src } from './colors';
import { leaves, values } from './dtcg';
import { TOKENS_PRIMITIVES } from './primitives';
export { DS_COLOR } from './colors';
export { contrastRatio } from './contrast';
export { PRIMITIVES } from './primitives';
export { SEMANTICS_SRC, TOKENS_SEMANTICS, resolve } from './semantics';
export { LEGACY_SEMANTICS_SRC, TOKENS_LEGACY_SEMANTICS, resolveLegacy, nest, toPascal, } from './semantics';
/** units of spacing in px */
const DS_SHAPE_SPACING_src = leaves('dimension', {
    /** Basic unit */
    BASE: 4,
    /** Between section and next section */
    SECTION: 40,
    /** Between title/heading and descriptive text */
    HEADING_DESCRIPTION: 8,
    /** Between heading and content on desktop */
    HEADING_CONTENT_DESKTOP: 24,
    /** Between heading and content on mobile */
    HEADING_CONTENT_MOBILE: 16,
    /** Between pieces of related content */
    CONTENT: 24,
    /** Between edge of viewport and content > large breakpoint */
    SIDE_LARGE: 32,
    /** Between edge of viewport and content medium to large breakpoint */
    SIDE_MEDIUM: 24,
    /** Between edge of viewport and content small to medium breakpoint and below */
    SIDE_SMALL: 16,
});
export const DS_SHAPE_SPACING = values(DS_SHAPE_SPACING_src);
/** units of spacing in base spacing units */
const DS_SPACING_UNITS_src = leaves('dimension', {
    /** Basic unit */
    BASE: 1,
    /** Between section and next section */
    SECTION: DS_SHAPE_SPACING.SECTION / DS_SHAPE_SPACING.BASE,
    /** Between title/heading and descriptive text */
    HEADING_DESCRIPTION: DS_SHAPE_SPACING.HEADING_DESCRIPTION / DS_SHAPE_SPACING.BASE,
    /** Between heading and content on desktop */
    HEADING_CONTENT_DESKTOP: DS_SHAPE_SPACING.HEADING_CONTENT_DESKTOP / DS_SHAPE_SPACING.BASE,
    /** Between heading and content on mobile */
    HEADING_CONTENT_MOBILE: DS_SHAPE_SPACING.HEADING_CONTENT_MOBILE / DS_SHAPE_SPACING.BASE,
    /** Between pieces of related content */
    CONTENT: DS_SHAPE_SPACING.CONTENT / DS_SHAPE_SPACING.BASE,
    /** Between edge of viewport and content > large breakpoint */
    SIDE_LARGE: DS_SHAPE_SPACING.SIDE_LARGE / DS_SHAPE_SPACING.BASE,
    /** Between edge of viewport and content medium to large breakpoint */
    SIDE_MEDIUM: DS_SHAPE_SPACING.SIDE_MEDIUM / DS_SHAPE_SPACING.BASE,
    /** Between edge of viewport and content small to medium breakpoint and below */
    SIDE_SMALL: DS_SHAPE_SPACING.SIDE_SMALL / DS_SHAPE_SPACING.BASE,
});
export const DS_SPACING_UNITS = values(DS_SPACING_UNITS_src);
/** Heights of various elements in px */
const DS_HEIGHT_src = leaves('dimension', {
    AVATAR_XLARGE: 96,
    AVATAR_LARGE: 64,
    AVATAR_MEDIUM: 48,
    AVATAR_SMALL: 40,
    AVATAR_XSMALL: 32,
    AVATAR_XXSMALL: 24,
    QR_CODE: 164,
    BUTTON_XLARGE: 56,
    BUTTON_LARGE: 48,
    BUTTON_MEDIUM: 36,
    BUTTON_SMALL: 32,
    BUTTON_XSMALL: 24,
    // For the circular FAB this height doubles as the box width (Figma:
    // small 40, medium 48). Circular Large is MUI's 56×56 default and needs
    // no token. The Extended (pill) variant at Large is 50px tall in Figma,
    // hence FAB_EXTENDED_LARGE — it sizes only the height, not a width.
    // Kept distinct from the BUTTON_* scale: those carry MUI's minHeight:36
    // floor semantics, which previously squashed the small FAB to 40×36.
    FAB_SMALL: 40,
    FAB_MEDIUM: 48,
    FAB_EXTENDED_LARGE: 50,
    ICON_BUTTON_SMALL: 32,
    ICON_BUTTON_LEGACY_MEDIUM: 40,
    ICON_BUTTON_MEDIUM: 48,
    ICON_BUTTON_LARGE_PLAYER_CONTROL: 56,
    ICON_BUTTON_LARGE: 64,
    CHIP_XSMALL: 18,
    TAB: 42,
    TOOLBAR: 64,
    TOOLBAR_MOBILE: 56,
    HORIZONTAL_CARD_SIZE: 116,
    BANNER_SIZE_SMALL: 88,
    BANNER_SIZE_MEDIUM: 116,
    BANNER_SIZE_LARGE: 140,
    PROGRESS_CIRCLE_SIZE_MD: 88,
    CARD_MEDIUM: 152,
    LOGO_SMALL: 64,
    LOGO_MEDIUM: 96,
    IMAGE_SMALL: 196,
    IMAGE_MEDIUM: 224,
    IMAGE_LARGE: 364,
    IMAGE_XLARGE: 500,
    SKELETON_CONTENT: 152,
});
export const DS_HEIGHT = values(DS_HEIGHT_src);
/** Heights of various elements in units of spacing */
const DS_HEIGHT_UNITS_src = leaves('dimension', {
    AVATAR_XLARGE: DS_HEIGHT.AVATAR_XLARGE / DS_SHAPE_SPACING.BASE,
    AVATAR_LARGE: DS_HEIGHT.AVATAR_LARGE / DS_SHAPE_SPACING.BASE,
    AVATAR_MEDIUM: DS_HEIGHT.AVATAR_MEDIUM / DS_SHAPE_SPACING.BASE,
    AVATAR_SMALL: DS_HEIGHT.AVATAR_SMALL / DS_SHAPE_SPACING.BASE,
    AVATAR_XSMALL: DS_HEIGHT.AVATAR_XSMALL / DS_SHAPE_SPACING.BASE,
    AVATAR_XXSMALL: DS_HEIGHT.AVATAR_XXSMALL / DS_SHAPE_SPACING.BASE,
    BUTTON_LARGE: DS_HEIGHT.BUTTON_LARGE / DS_SHAPE_SPACING.BASE,
    BUTTON_MEDIUM: DS_HEIGHT.BUTTON_MEDIUM / DS_SHAPE_SPACING.BASE,
    BUTTON_SMALL: DS_HEIGHT.BUTTON_SMALL / DS_SHAPE_SPACING.BASE,
    BUTTON_XSMALL: DS_HEIGHT.BUTTON_XSMALL / DS_SHAPE_SPACING.BASE,
    CHIP_XSMALL: DS_HEIGHT.CHIP_XSMALL / DS_SHAPE_SPACING.BASE,
    TOOLBAR: DS_HEIGHT.TOOLBAR / DS_SHAPE_SPACING.BASE,
    TOOLBAR_MOBILE: DS_HEIGHT.TOOLBAR_MOBILE / DS_SHAPE_SPACING.BASE,
    HORIZONTAL_CARD_SIZE: DS_HEIGHT.HORIZONTAL_CARD_SIZE / DS_SHAPE_SPACING.BASE,
    BANNER_SIZE_SMALL: DS_HEIGHT.BANNER_SIZE_SMALL / DS_SHAPE_SPACING.BASE,
    BANNER_SIZE_MEDIUM: DS_HEIGHT.BANNER_SIZE_MEDIUM / DS_SHAPE_SPACING.BASE,
    BANNER_SIZE_LARGE: DS_HEIGHT.BANNER_SIZE_LARGE / DS_SHAPE_SPACING.BASE,
    PROGRESS_CIRCLE_SIZE_MD: DS_HEIGHT.PROGRESS_CIRCLE_SIZE_MD / DS_SHAPE_SPACING.BASE,
    CARD_MEDIUM: DS_HEIGHT.CARD_MEDIUM / DS_SHAPE_SPACING.BASE,
    LOGO_MEDIUM: DS_HEIGHT.LOGO_MEDIUM / DS_SHAPE_SPACING.BASE,
    IMAGE_LARGE: DS_HEIGHT.IMAGE_LARGE / DS_SHAPE_SPACING.BASE,
});
export const DS_HEIGHT_UNITS = values(DS_HEIGHT_UNITS_src);
/** Widths of various elements in px */
const DS_WIDTH_src = leaves('dimension', {
    BUTTON_XSMALL: 24,
    BUTTON_MEDIUM: 140,
    BUTTON_LARGE: 440,
    FORM_MAX_WIDTH: 442,
    CONTENT_MAX_WIDTH: 560,
    LOGO_SMALL: 64,
    LOGO_MEDIUM: 96,
    CHIP_XSMALL: 18,
    IMAGE_SMALL: 196,
    IMAGE_MEDIUM: 224,
    IMAGE_LARGE: 364,
    IMAGE_XLARGE: 500,
});
export const DS_WIDTH = values(DS_WIDTH_src);
/** Heights of various elements in units of spacing */
const DS_WIDTH_UNITS_src = leaves('dimension', {
    BUTTON_XSMALL: DS_WIDTH.BUTTON_XSMALL / DS_SHAPE_SPACING.BASE,
    BUTTON_MEDIUM: DS_WIDTH.BUTTON_MEDIUM / DS_SHAPE_SPACING.BASE,
    BUTTON_LARGE: DS_WIDTH.BUTTON_LARGE / DS_SHAPE_SPACING.BASE,
    FORM_MAX_WIDTH: DS_WIDTH.FORM_MAX_WIDTH / DS_SHAPE_SPACING.BASE,
    LOGO_MEDIUM: DS_WIDTH.LOGO_MEDIUM / DS_SHAPE_SPACING.BASE,
    CHIP_XSMALL: DS_WIDTH.CHIP_XSMALL / DS_SHAPE_SPACING.BASE,
    IMAGE_LARGE: DS_WIDTH.IMAGE_LARGE / DS_SHAPE_SPACING.BASE,
});
export const DS_WIDTH_UNITS = values(DS_WIDTH_UNITS_src);
/** Used in border-radius in px */
const DS_SHAPE_BORDER_RADIUS_src = leaves('dimension', {
    SMALL: 4,
    MEDIUM: 8,
    LARGE: 12,
    CARD: 12,
    AVATAR_ROUNDED: 4,
    BUTTON_LARGE: DS_HEIGHT.BUTTON_LARGE / 2,
    BUTTON_MEDIUM: DS_HEIGHT.BUTTON_MEDIUM / 2,
    BUTTON_SMALL: DS_HEIGHT.BUTTON_SMALL / 2,
});
export const DS_SHAPE_BORDER_RADIUS = values(DS_SHAPE_BORDER_RADIUS_src);
/** Viewport breakpoints in px */
const DS_BREAKPOINT_src = leaves('dimension', {
    /** Very small viewports */
    INTRA: 0,
    /** Phones in portrait mode */
    SMALL: 320,
    /** Tablet in portrait mode */
    MEDIUM: 600,
    /** Phone in landscape, tablet in landscape, desktop */
    LARGE: 840,
    /** Very large viewports, used to determine when a layout should stop expanding */
    ULTRA: 1440,
});
export const DS_BREAKPOINT = values(DS_BREAKPOINT_src);
/** Used in font-family  */
const DS_FONT_FAMILY_src = {
    $value: '"Figtree", -apple-system, Arial, BlinkMacSystemFont, Helvetica Neue, Helvetica, sans-serif',
    $type: 'fontFamily',
};
export const DS_FONT_FAMILY = DS_FONT_FAMILY_src.$value;
/** Used in font-size in px */
const DS_FONT_SIZE_src = leaves('dimension', {
    H1: 96,
    H2: 60,
    H3: 48,
    H4: 34,
    H5: 24,
    H6: 20,
    AVATAR: 20,
    NORMAL_LARGE: 18,
    NORMAL_MEDIUM: 16,
    NORMAL_SMALL: 14,
    BUTTON_LARGE: 15,
    BUTTON_MEDIUM: 14, // medium button should not be used
    BUTTON_SMALL: 13,
    LABEL: 12,
    TOOLTIP: 10,
    ICON_BUTTON_XSMALL: 16,
    ICON_BUTTON_SMALL: 24,
    ICON_BUTTON_MEDIUM: 24,
    ICON_BUTTON_LARGE_PLAYER_CONTROL: 39,
    ICON_BUTTON_LARGE: 40,
    ICON_BUTTON_PLAYER_CONTROL: 32,
    PROGRESS_CIRCLE_SM: 40,
    PROGRESS_CIRCLE_MD: 56,
});
export const DS_FONT_SIZE = values(DS_FONT_SIZE_src);
/**
 * Used in letter-spacing. Values are px-suffixed strings, so they are declared
 * `'string'` (the opaque passthrough $type — see dtcg.ts), not `'dimension'`:
 * they carry a unit baked into the string and emit verbatim. A future numeric
 * dimension transform (px->dp/pt) must not pick these up.
 */
const DS_LETTER_SPACING_src = leaves('string', {
    L_2: '-1.5px',
    L_1: '-0.5px',
    L0: '0px',
    L1: '0.1px',
    L2: '0.14px',
    L3: '0.15px',
    L4: '0.25px',
    L5: '0.4px',
    L6: '0.46px',
    L7: '1px',
});
export const DS_LETTER_SPACING = values(DS_LETTER_SPACING_src);
/**
 * Used in line-height. Values are px strings because line-height is unitless in
 * emotion — a bare number renders as a multiplier, not pixels. Declared
 * `'string'` (opaque passthrough — see dtcg.ts), not `'dimension'`, for the same
 * reason as DS_LETTER_SPACING: the unit is baked into the string.
 */
const DS_LINE_HEIGHT_src = leaves('string', {
    BUTTON_LARGE: '26px',
    BUTTON_MEDIUM: '24px',
    BUTTON_SMALL: '22px',
    CHIP_XSMALL: '18px',
});
export const DS_LINE_HEIGHT = values(DS_LINE_HEIGHT_src);
/** Used in font-weight */
const DS_FONT_WEIGHT_src = leaves('fontWeight', {
    ULTRA_LIGHT: 100,
    LIGHT: 300,
    REGULAR: 400,
    MEDIUM: 500,
    DEMI: 600,
    BOLD: 700,
    ULTRA_BOLD: 900,
});
export const DS_FONT_WEIGHT = values(DS_FONT_WEIGHT_src);
/** Available gradients, can be used in the background property. `'string'` =
 * opaque passthrough (composite CSS, emitted verbatim — see dtcg.ts). */
const DS_GRADIENT_src = leaves('string', {
    LIGHT_BACKGROUND_PRIMARY: `linear-gradient(180deg, ${DS_COLOR.LIGHT_GRADIENT_BACKGROUND_PRIMARY_TOP} 0%, ${DS_COLOR.LIGHT_GRADIENT_BACKGROUND_PRIMARY_BOTTOM} 100%),linear-gradient(0deg, ${DS_COLOR.GRADIENT_SHADER_DARK}, ${DS_COLOR.GRADIENT_SHADER_DARK})`,
    LIGHT_BACKGROUND_SECONDARY: `linear-gradient(180deg, ${DS_COLOR.LIGHT_GRADIENT_BACKGROUND_SECONDARY_TOP} 0%, ${DS_COLOR.LIGHT_GRADIENT_BACKGROUND_SECONDARY_BOTTOM} 100%),linear-gradient(0deg, ${DS_COLOR.GRADIENT_SHADER_LIGHT}, ${DS_COLOR.GRADIENT_SHADER_LIGHT}),linear-gradient(0deg, ${DS_COLOR.GRADIENT_SHADER_DARK}, ${DS_COLOR.GRADIENT_SHADER_DARK})`,
    LIGHT_BACKGROUND_VIDEO: `linear-gradient(0deg, ${DS_COLOR.LIGHT_GRADIENT_BACKGROUND_VIDEO_TOP} 0%, ${DS_COLOR.LIGHT_GRADIENT_BACKGROUND_VIDEO_BOTTOM} 100%)`,
    LIGHT_ICON_PRIMARY: `linear-gradient(135deg, ${DS_COLOR.LIGHT_GRADIENT_ICON_PRIMARY_TOP} 0%, ${DS_COLOR.LIGHT_GRADIENT_ICON_PRIMARY_BOTTOM} 100%)`,
    LIGHT_ICON_SECONDARY: `linear-gradient(135deg, ${DS_COLOR.LIGHT_GRADIENT_ICON_SECONDARY_TOP} 0%, ${DS_COLOR.LIGHT_GRADIENT_ICON_SECONDARY_BOTTOM} 100%)`,
    LIGHT_HERO: `linear-gradient(180deg, ${DS_COLOR.LIGHT_GRADIENT_HERO_TOP} 0%, ${DS_COLOR.LIGHT_GRADIENT_BACKGROUND_PRIMARY_TOP} 100%)`,
    DARK_BACKGROUND_PRIMARY: `linear-gradient(180deg, ${DS_COLOR.DARK_GRADIENT_BACKGROUND_PRIMARY_TOP} 0%, ${DS_COLOR.DARK_GRADIENT_BACKGROUND_PRIMARY_BOTTOM} 100%)`,
    DARK_BACKGROUND_SECONDARY: `linear-gradient(180deg, ${DS_COLOR.DARK_GRADIENT_BACKGROUND_SECONDARY_TOP} 0%, ${DS_COLOR.DARK_GRADIENT_BACKGROUND_SECONDARY_BOTTOM} 100%),linear-gradient(0deg, ${DS_COLOR.GRADIENT_SHADER_DARK}, ${DS_COLOR.GRADIENT_SHADER_DARK})`,
    DARK_BACKGROUND_VIDEO: `linear-gradient(0deg, ${DS_COLOR.DARK_GRADIENT_BACKGROUND_VIDEO_TOP} 0%, ${DS_COLOR.DARK_GRADIENT_BACKGROUND_VIDEO_BOTTOM} 100%)`,
    DARK_ICON_PRIMARY: `linear-gradient(180deg, ${DS_COLOR.DARK_GRADIENT_ICON_PRIMARY_TOP} 0%, ${DS_COLOR.DARK_GRADIENT_ICON_PRIMARY_BOTTOM} 100%)`,
    DARK_ICON_SECONDARY: `linear-gradient(180deg, ${DS_COLOR.DARK_GRADIENT_ICON_SECONDARY_TOP} 0%, ${DS_COLOR.DARK_GRADIENT_ICON_SECONDARY_BOTTOM} 100%)`,
    DARK_HERO: `linear-gradient(180deg, ${DS_COLOR.DARK_GRADIENT_HERO_TOP} 0%, ${DS_COLOR.DARK_GRADIENT_BACKGROUND_HERO_TOP} 100%)`,
    CALM_HEALTH: 'linear-gradient(252deg, rgba(200, 231, 255, 0.50) 0%, rgba(84, 132, 213, 0.00) 50%), linear-gradient(51deg, #BB91E9 1.06%, rgba(187, 145, 233, 0.00) 42.51%), linear-gradient(108deg, #4667C4 0%, #5286D7 49.48%, #7193DF 100%)',
    CALM_BRAND: `linear-gradient(180deg, ${DS_COLOR.CALM_GRADIENT_BRAND_TOP} 0%, ${DS_COLOR.CALM_GRADIENT_BRAND_BOTTOM} 100%)`,
    CALM_BRAND_ACCESSIBLE: `linear-gradient(180deg, ${DS_COLOR.CALM_GRADIENT_BRAND_ACCESSIBLE_TOP} 0%, ${DS_COLOR.CALM_GRADIENT_BRAND_ACCESSIBLE_BOTTOM} 100%)`,
});
export const DS_GRADIENT = values(DS_GRADIENT_src);
/** `'string'` = opaque passthrough (composite CSS shadow, emitted verbatim — see dtcg.ts). */
const DS_BOX_SHADOW_src = leaves('string', {
    POD: `0 4px 16px 0 ${DS_COLOR.SHADOW_POD}`,
});
export const DS_BOX_SHADOW = values(DS_BOX_SHADOW_src);
const DS_Z_INDEX_src = leaves('number', {
    MOBILE_STEPPER: 1000,
    SPEED_DIAL_AND_FAB: 1050,
    APP_BAR: 1100,
    DRAWER: 1200,
    AUDIO_SR_CONTROLS: 1225,
    SKIP_TO_MAIN: 1250,
    MOBILE_PROMO_DRAWER: 1275,
    MODAL: 1300,
    MEDIA_CONTROLS: 1313,
    AUDIO_PLAYER_UPPER_ACTIONS: 1325,
    LOADING_DIALOG: 1350,
    COOKIE_DIALOG: 1375,
    SNACKBAR: 1400,
    TOOLTIP: 1500,
    CLICKABLE_CARD: 2,
});
export const DS_Z_INDEX = values(DS_Z_INDEX_src);
/**
 * Typed DTCG token tree — the source of truth the cross-platform exporter reads.
 * Each leaf carries a declared `$type`, so the exporter never sniffs values to
 * decide how to emit them. The public `DS_*` exports above are derived from these
 * same `_src` groups via `values()`, so there is a single source with no
 * duplicated data.
 */
export const TOKENS = {
    DS_SHAPE_SPACING: DS_SHAPE_SPACING_src,
    DS_SPACING_UNITS: DS_SPACING_UNITS_src,
    DS_HEIGHT: DS_HEIGHT_src,
    DS_HEIGHT_UNITS: DS_HEIGHT_UNITS_src,
    DS_WIDTH: DS_WIDTH_src,
    DS_WIDTH_UNITS: DS_WIDTH_UNITS_src,
    DS_SHAPE_BORDER_RADIUS: DS_SHAPE_BORDER_RADIUS_src,
    DS_BREAKPOINT: DS_BREAKPOINT_src,
    DS_FONT_FAMILY: DS_FONT_FAMILY_src,
    DS_FONT_SIZE: DS_FONT_SIZE_src,
    DS_LETTER_SPACING: DS_LETTER_SPACING_src,
    DS_LINE_HEIGHT: DS_LINE_HEIGHT_src,
    DS_FONT_WEIGHT: DS_FONT_WEIGHT_src,
    DS_COLOR: DS_COLOR_src,
    DS_GRADIENT: DS_GRADIENT_src,
    DS_BOX_SHADOW: DS_BOX_SHADOW_src,
    DS_Z_INDEX: DS_Z_INDEX_src,
    Primitives: TOKENS_PRIMITIVES,
    // TOKENS_SEMANTICS is intentionally not registered here: its {light, dark}
    // leaves aren't single-$value DTCG tokens the exporter understands, and the
    // emit must stay byte-identical. It's exported separately for a later step.
};
//# sourceMappingURL=index.js.map